/* eslint-disable react/no-array-index-key */
import Head from "next/head";
import { useState, useContext, useEffect, useMem } from "react";
import { useRouter } from "next/router";
import ReactMarkdown from "react-markdown";
import useSWR from "swr";
import dynamic from "next/dynamic";

import AppContext from "context/app";
import Breadcrumb from "components/global/breadcrumbs";
import Cover from "components/global/cover";
import Throw404 from "components/throw404";
import Sections from "components/sections";
import SectionText from "components/sections/SectionText";
import Card from "components/global/card";
import Attributes from "components/global/attributes";
import Gallery from "components/global/gallery";

import PARK_QUERY from "queries/park";

const EditPanel = dynamic(() => import("components/parks/edit"), {
  ssr: false,
});
const EditLink = dynamic(() => import("components/admin/editlink"), {
  ssr: false,
});

export default function ParkDetails() {
  const { setRecommendedRecentPark, setRecommendedRecentResort } =
    useContext(AppContext);

  const [editOpen, setEditOpen] = useState(false);

  const router = useRouter();

  const variables = {
    slug: router.query.park,
  };

  const { data, error } = useSWR([PARK_QUERY, variables]);

  if (error) {
    console.log(error);
  }

  useEffect(() => {
    const park = data?.parks[0];

    setRecommendedRecentPark(park);

    if (park?.Resort) {
      setRecommendedRecentResort(park.Resort);
    }
  }, [data?.parks, setRecommendedRecentPark, setRecommendedRecentResort]);

  const park = data?.parks[0];

  if (!Array.isArray(data?.parks) || !data?.parks?.length) {
    return <Throw404 />;
  }

  let description = `Information on rides, shows and other attractions found at ${park.Name}`;

  if (park.Resort) {
    description = `${description} in ${park.Resort.Name}`;
  }

  let CoverPhoto = "";
  let ParkTitle = "";
  if (park.CoverPhoto || park.MainPhoto) {
    CoverPhoto = (
      <Cover title={park.Name} image={park.CoverPhoto || park.MainPhoto} />
    );
  } else {
    ParkTitle = (
      <h2 className="mb-10 font-serif text-5xl text-gray-800">{park.Name}</h2>
    );
  }

  const breadcrumbs = [];

  if (Array.isArray(park.Companies) && park.Companies.length > 0) {
    const company = park.Companies[0];
    breadcrumbs.push({
      name: company.Name,
      link: "/company/[company]",
      linkAs: `/company/${company.Slug}`,
    });
  }

  if (park.Resort) {
    const resort = park.Resort;
    breadcrumbs.push({
      name: resort.Name,
      link: "/resort/[resort]",
      linkAs: `/resort/${resort.Slug}`,
    });
  }

  breadcrumbs.push({
    name: park.Name,
    link: "/park/[park]",
    linkAs: `/park/${park.Slug}`,
  });

  const attributes = [
    {
      ValueType: "Status",
      Value: park.CurrentlyOperating ? "Operational" : "Closed",
    },
    {
      ValueType: "Type",
      Value: park.Type,
    },
  ];

  // Todo handle these better, yuck
  if (park.OpeningDate) {
    attributes.push({
      ValueType: "Opening Date",
      Value: park.OpeningDate,
    });
  }

  if (park.ClosedDate) {
    attributes.push({
      ValueType: "Date Closed",
      Value: park.ClosedDate,
    });
  }

  if (park.MaxCapacity) {
    attributes.push({
      ValueType: "Capacity",
      Value: park.MaxCapacity,
    });
  }

  // find landless attractions
  const landAttractions = new Set();
  const allAttractions = new Set();

  park?.Attractions?.forEach((attraction) => {
    allAttractions.add(attraction.id);
  });

  park?.Lands?.forEach((land) => {
    land?.Attractions?.forEach((attraction) => {
      landAttractions.add(attraction.id);
    });
  });

  let landlessAttractions = Array.from(allAttractions).filter(
    (attraction) => !landAttractions.has(attraction)
  );
  landlessAttractions = landlessAttractions.map((id) => {
    return park?.Attractions?.find((attraction) => attraction?.id === id);
  });

  // cleanup some memory
  landAttractions.clear();
  allAttractions.clear();

  return (
    <>
      <Head>
        <title>{park.Name} | Open Coaster</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={park.Name} />
        <meta property="og:description" content={description} />
        <meta
          property="og:url"
          content={`https://opencoaster.com/park/${park.Slug}`}
        />
        {park.MainPhoto && (
          <>
            <meta
              property="og:image"
              content={`https://images.opencoaster.com/1200x630/${park.MainPhoto.hash}${park.MainPhoto.ext}`}
            />
            <meta
              name="twitter:image"
              content={`https://images.opencoaster.com/1200x600/${park.MainPhoto.hash}${park.MainPhoto.ext}`}
            />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta
              property="og:image:alt"
              content={
                park.MainPhoto.alternativeText
                  ? park.MainPhoto.alternativeText
                  : park.Name
              }
            />
            <meta name="twitter:card" content="summary_large_image" />
          </>
        )}
      </Head>
      <EditPanel open={editOpen} setOpen={setEditOpen} park={park} />
      {CoverPhoto}
      <main className="container m-6 mx-auto px-6 sm:px-0">
        {ParkTitle}

        <Breadcrumb items={breadcrumbs} />

        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          <div className="order-last md:order-first md:col-span-2">
            <Sections sections={park.Sections} />

            {Array.isArray(park.SeasonalEvent) &&
              park.SeasonalEvent.length > 0 && (
                <div className="mb-10">
                  <h3 className="mb-3 font-serif text-4xl text-gray-800">
                    {park.Lands.length > 1
                      ? "Seasonal Events"
                      : "Seasonal Event"}
                    :
                  </h3>

                  {park.SeasonalEvent.map((event) => (
                    <div key={`event-${event.id}`} className="mb-3">
                      <h4 className="mb-3 text-2xl text-gray-800">
                        {event.Name}
                      </h4>
                      <div className="prose mb-3 lg:prose-xl">
                        <ReactMarkdown source={event.Description} />
                      </div>
                    </div>
                  ))}
                </div>
              )}

            {Array.isArray(park.Attractions) &&
              park.Attractions.length > 0 &&
              (!Array.isArray(park.Lands) || park.Lands.length === 0) && (
                <div className="mb-10 overflow-hidden rounded-lg bg-white shadow">
                  <div className="px-4 py-5 sm:p-6">
                    <>
                      <div className="mb-5 border-b border-gray-200 pb-5">
                        <h3 className="flex-1 text-2xl font-bold text-gray-900">
                          {park.Attractions.length > 1
                            ? "Attractions"
                            : "Attraction"}
                        </h3>
                      </div>
                      <div className="mb-10 last-of-type:mb-0">
                        <div className="grid grid-cols-2 gap-8 last:mb-0 md:grid-cols-2 lg:grid-cols-4">
                          {park.Attractions.map((attraction) => (
                            <Card
                              link={`/park/${park.Slug}/${attraction.Slug}`}
                              linkAs="/park/[park]/[attraction]"
                              title={attraction.Name}
                              image={attraction.MainPhoto}
                              type="attraction"
                              location={attraction?.Location}
                              key={`attraction-${attraction.id}`}
                            />
                          ))}
                        </div>
                      </div>
                    </>
                  </div>
                </div>
              )}

            {Array.isArray(park.Lands) && park.Lands.length > 0 && (
              <div className="mb-10 overflow-hidden rounded-lg bg-white shadow">
                <div className="px-4 py-5 sm:p-6">
                  <>
                    <div className="mb-5 border-b border-gray-200 pb-5">
                      <h3 className="flex-1 text-2xl font-bold text-gray-900">
                        Attractions
                      </h3>
                    </div>

                    {landlessAttractions?.length > 0 && (
                      <div className="mb-10 last-of-type:mb-0">
                        <div className="grid grid-cols-2 gap-8 last:mb-0 md:grid-cols-2 lg:grid-cols-4">
                          {landlessAttractions.map((attraction) => (
                            <Card
                              link={`/park/${park.Slug}/${attraction.Slug}`}
                              linkAs="/park/[park]/[attraction]"
                              title={attraction.Name}
                              image={attraction.MainPhoto}
                              type="attraction"
                              location={attraction?.Location}
                              key={`attraction-card-${attraction.id}`}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {park.Lands.map((land, i) => (
                      <div
                        key={`land-${i}`}
                        className="mb-10 last-of-type:mb-0"
                      >
                        <h4 className="mb-3 text-xl font-medium text-gray-900">
                          {land?.Name}
                        </h4>

                        {land?.Content?.length > 0 && (
                          <SectionText section={land} />
                        )}
                        {Array.isArray(land?.Attractions) &&
                          land.Attractions.length > 0 && (
                            <div className="grid grid-cols-2 gap-8 last:mb-0 md:grid-cols-2 lg:grid-cols-4">
                              {land.Attractions.map((attraction) => (
                                <Card
                                  link={`/park/${park.Slug}/${attraction.Slug}`}
                                  linkAs="/park/[park]/[attraction]"
                                  title={attraction.Name}
                                  image={attraction.MainPhoto}
                                  type="attraction"
                                  location={attraction?.Location}
                                  key={`attraction-land-card-${attraction.id}`}
                                />
                              ))}
                            </div>
                          )}
                      </div>
                    ))}
                  </>
                </div>
              </div>
            )}

            <Gallery item={park} />
          </div>
          <div className="order-first md:order-last md:col-span-1">
            <ul role="list" className="space-y-3">
              <Attributes attributes={attributes} title="Key Facts" />
              <EditLink setEditOpen={setEditOpen} />
            </ul>
          </div>
        </div>
      </main>
    </>
  );
}
